import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { ClientOnly } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { PageViewTracker } from "../components/PageViewTracker";
import { Toaster } from "../components/ui/sonner";
import { getPricingSettings } from "../lib/pricing.functions";

// Heavy, below-the-fold calendar widget: keep it out of the initial bundle.
const AvailabilityWidget = lazy(() =>
  import("../components/AvailabilityWidget").then((m) => ({ default: m.AvailabilityWidget })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Casa Mi Pajobe, Vakantievilla met privézwembad, Costa Blanca" },
      { name: "description", content: "Vrijstaande vakantievilla met privézwembad en grote buitenkeuken in La Nucia, Costa Blanca. 4 slaapkamers, tot 8 personen." },
      { property: "og:title", content: "Casa Mi Pajobe, Vakantievilla met privézwembad, Costa Blanca" },
      { property: "og:description", content: "Vrijstaande vakantievilla met privézwembad en grote buitenkeuken in La Nucia, Costa Blanca. 4 slaapkamers, tot 8 personen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Casa Mi Pajobe, Vakantievilla met privézwembad, Costa Blanca" },
      { name: "twitter:description", content: "Vrijstaande vakantievilla met privézwembad en grote buitenkeuken in La Nucia, Costa Blanca. 4 slaapkamers, tot 8 personen." },
      { property: "og:image", content: "https://www.casamipajobe.com/__l5e/assets-v1/1c7d9044-e1c0-44c1-a661-425b270f95a5/og-preview.jpeg" },
      { name: "twitter:image", content: "https://www.casamipajobe.com/__l5e/assets-v1/1c7d9044-e1c0-44c1-a661-425b270f95a5/og-preview.jpeg" },
      { name: "google-site-verification", content: "-tThd6aVixVscn1iz5RK6gi3loZKBTrzuBIBuaAy9Y0" },
      { name: "google-site-verification", content: "xspWME-75lRcGd4s5DKPAsZV3_-a0aDZu9Xszuzc848" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon.png", type: "image/png", sizes: "64x64" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        src: "https://static.opinly.ai/p.js",
        async: true,
        "data-key": "pk-Hg5ZkadrC7xS7aNDHWagj2kh2nMXtzYUjMSd8G6",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <div className="flex min-h-screen flex-col">
        <AnnouncementBar />
        <div className="flex-1">
          <Outlet />
        </div>
        <div className="border-t border-border/60 bg-background py-3 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Benjamin Rysman. All rights reserved.
        </div>
        <MobileDesktopHint />
        <PageViewTracker />
        <WhatsAppContact />
        <AvailabilityFloating />
        <Toaster position="top-center" richColors />
      </div>
    </QueryClientProvider>
  );
}

type BarLang = "en" | "nl" | "fr" | "de";

const WHATSAPP_NUMBER = "32475921693";

const WHATSAPP_LABELS: Record<BarLang, { aria: string; tooltip: string; message: string }> = {
  en: {
    aria: "Contact us on WhatsApp",
    tooltip: "Chat with us on WhatsApp",
    message: "Hello, I have a question about Casa Mi Pajobe.",
  },
  nl: {
    aria: "Contacteer ons via WhatsApp",
    tooltip: "Chat met ons via WhatsApp",
    message: "Hallo, ik heb een vraag over Casa Mi Pajobe.",
  },
  fr: {
    aria: "Contactez-nous sur WhatsApp",
    tooltip: "Discutez avec nous sur WhatsApp",
    message: "Bonjour, j'ai une question concernant Casa Mi Pajobe.",
  },
  de: {
    aria: "Kontaktieren Sie uns über WhatsApp",
    tooltip: "Chatten Sie mit uns auf WhatsApp",
    message: "Hallo, ich habe eine Frage zu Casa Mi Pajobe.",
  },
};

function WhatsAppContact() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const seg = pathname.split("/").filter(Boolean)[0];
  const lang: BarLang =
    seg === "nl" || seg === "fr" || seg === "de" || seg === "en" ? (seg as BarLang) : "en";
  const { aria, tooltip, message } = WHATSAPP_LABELS[lang];
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

  // Niet tonen in het adminportaal.
  if (seg === "admin" || seg === "auth") return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={aria}
      title={tooltip}
      className="fixed bottom-5 right-5 z-[90] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg ring-1 ring-black/5 transition hover:scale-105 hover:bg-[#1ebe57] focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        className="h-7 w-7"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.13-.602.13-.945 0-.144-.013-.36-.027-.602-.057-.602-1.602-1.146-2.107-1.146zm-2.92 9.078c-1.69 0-3.337-.45-4.77-1.318l-.34-.2-3.51.92.94-3.422-.224-.36C7.197 20.5 6.68 18.694 6.68 16.83c0-5.232 4.252-9.484 9.484-9.484 5.226 0 9.49 4.252 9.49 9.484-.012 5.232-4.276 9.474-9.487 9.474zm0-20.5C9.806 5.783 4.93 10.66 4.93 16.83c0 1.948.484 3.853 1.4 5.53L4.93 28.124l5.926-1.387a11.39 11.39 0 0 0 5.335 1.34c6.392 0 11.39-4.876 11.39-11.246-.014-6.18-4.876-11.044-11.39-11.044z"/>
      </svg>
    </a>
  );
}

function AvailabilityFloating() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Hide on booking flow, admin, and other tool pages where a full calendar exists.
  if (
    pathname.startsWith("/book") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/thank-you") ||
    pathname.startsWith("/review") ||
    pathname.startsWith("/contract") ||
    pathname.startsWith("/guest-info") ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/api")
  ) {
    return null;
  }
  const seg = pathname.split("/").filter(Boolean)[0];
  const lang: BarLang =
    seg === "nl" || seg === "fr" || seg === "de" || seg === "en" ? (seg as BarLang) : "en";
  return (
    <ClientOnly fallback={null}>
      <Suspense fallback={null}>
        <AvailabilityWidget lang={lang} />
      </Suspense>
    </ClientOnly>
  );
}

const ANNOUNCEMENT: Record<BarLang, { text: string; cta: string }> = {
  en: { text: "Last-minute deal, 20% off stays checking out by 11 July", cta: "Book now" },
  nl: { text: "Last-minute, 20% korting bij check-out vóór 11 juli", cta: "Nu boeken" },
  fr: { text: "Offre last minute, -20% pour tout départ avant le 11 juillet", cta: "Réserver" },
  de: { text: "Last-Minute, 20% Rabatt bei Abreise bis 11. Juli", cta: "Jetzt buchen" },
};

function AnnouncementBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const fetchPricing = useServerFn(getPricingSettings);
  const { data: pricing } = useQuery({
    queryKey: ["pricing-settings"],
    queryFn: () => fetchPricing(),
    staleTime: 5 * 60_000,
  });
  if (pathname.startsWith("/book")) return null;
  if (!pricing?.lastMinuteEnabled || pricing.lastMinutePercent <= 0) return null;

  const seg = pathname.split("/").filter(Boolean)[0];
  const lang: BarLang =
    seg === "nl" || seg === "fr" || seg === "de" || seg === "en" ? (seg as BarLang) : "en";
  const { text, cta } = ANNOUNCEMENT[lang];
  const custom = (pricing.lastMinuteText?.[lang] ?? "").trim();
  const barText = custom
    ? custom.replace(/\{percent\}/g, String(pricing.lastMinutePercent))
    : text;

  return (
    <div
      className="w-full bg-announcement text-announcement-foreground px-4 py-2 text-center text-[13px] sm:text-sm leading-snug"
      style={{ minHeight: 40 }}
    >
      <span className="inline-flex flex-wrap items-center justify-center gap-x-2">
        <span>
          <span aria-hidden className="mr-1">🌞</span>
          {barText}
        </span>
        <span aria-hidden>·</span>
        <a
          href={`/book/${lang}`}
          className="inline-flex items-center rounded-full bg-announcement-foreground px-3 py-1 text-xs font-semibold text-announcement shadow-sm transition hover:opacity-90 cursor-pointer"
        >
          {cta} →
        </a>
      </span>
    </div>
  );
}

const HINT_MESSAGES: Record<BarLang, { title: string; body: string; cta: string }> = {
  en: {
    title: "Best viewed on desktop",
    body: "For the best experience exploring the villa, photos and booking calendar, we recommend using a PC or laptop.",
    cta: "Got it",
  },
  nl: {
    title: "Beste weergave op desktop",
    body: "Voor de beste ervaring met de villa, foto's en boekingskalender raden we een pc of laptop aan.",
    cta: "Begrepen",
  },
  fr: {
    title: "Optimisé pour ordinateur",
    body: "Pour une expérience optimale (villa, photos et calendrier de réservation), nous recommandons un PC ou un ordinateur portable.",
    cta: "Compris",
  },
  de: {
    title: "Am besten am Desktop",
    body: "Für das beste Erlebnis (Villa, Fotos und Buchungskalender) empfehlen wir einen PC oder Laptop.",
    cta: "Verstanden",
  },
};

function MobileDesktopHint() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("mobile-hint-dismissed") === "1") return;
    if (window.matchMedia("(max-width: 768px)").matches) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  if (!open) return null;

  const seg = pathname.split("/").filter(Boolean)[0];
  const lang: BarLang =
    seg === "nl" || seg === "fr" || seg === "de" || seg === "en" ? (seg as BarLang) : "en";
  const { title, body, cta } = HINT_MESSAGES[lang];

  const dismiss = () => {
    try {
      sessionStorage.setItem("mobile-hint-dismissed", "1");
    } catch {}
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-hint-title"
      className="fixed inset-0 z-[100] flex items-end justify-center bg-foreground/40 p-4 sm:items-center"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-card p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span aria-hidden className="text-2xl">💻</span>
          <div className="flex-1">
            <h2 id="mobile-hint-title" className="text-base font-semibold text-foreground">
              {title}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{body}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="mt-4 inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
